/* eslint-disable no-console */
import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Form, Button, Card, Alert, Spinner } from 'react-bootstrap';

import { IntegrationsList } from 'components';
import { getBulkConfigurations } from 'service/ConfigAPI';
import {
  fetchActiveIntegrationsPerLevel,
  getActiveIntegrationsByType,
  saveActiveIntegration,
} from 'rdx/hardware';
import { getPointOfSaleByID } from 'rdx/pointsOfSale';
import { integrationsByType } from 'rdx/hardware/installer/types';

function SelectIntegration({ integrationType }) {
  const dispatch = useDispatch();

  const navigate = useNavigate();

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { level, level_id } = useParams();
  const { pathname } = useLocation();
  const activePaymentIntegration = useSelector(
    getActiveIntegrationsByType(integrationType),
  );
  // console.log(activePaymentIntegration, 'activePaymentIntegration');
  const { warehouseID } = useSelector(getPointOfSaleByID(level_id));

  const [activeIntegrations, setActiveIntegrations] = useState([]);
  const [isFetching, setIsFetching] = useState(true);
  const [cafaConnectionError, setCafaConnectionError] = useState(false);
  const [cafaAuthError, setCafaAuthError] = useState(null);
  const [selectedIntegrationType, setSelectedIntegrationType] = useState();

  const getAllCAFAConfigs = async () => {
    setIsFetching(true);
    const promises = [];
    promises.push(
      getBulkConfigurations({
        level,
        level_id,
        type: integrationType,
      }),
    );

    if (level === 'Pos') {
      promises.push(
        getBulkConfigurations({
          level: 'Warehouse',
          level_id: warehouseID || '',
          type: integrationType,
        }),
      );
    }

    if (['Warehouse', 'Pos', 'User'].includes(level)) {
      promises.push(
        getBulkConfigurations({
          level: 'Company',
          level_id: '',
          type: integrationType,
        }),
      );
    }
    await Promise.all(promises)
      .then((responses) => {
        let integs = [];
        responses.forEach(({ data }) => {
          integs = integs.concat(data);
        });
        setCafaConnectionError(false);
        setCafaAuthError(false);
        setActiveIntegrations(integs);
        setIsFetching(false);
      })
      .catch(async (err) => {
        if (err?.response?.data?.message.includes('auth')) {
          setCafaAuthError(true);
        } else {
          setCafaConnectionError(true);
          setIsFetching(false);
        }
      });
  };

  // Fetch
  useEffect(() => {
    if (cafaAuthError === true || cafaAuthError === null) {
      getAllCAFAConfigs().then(() =>
        dispatch(fetchActiveIntegrationsPerLevel({ level, level_id })),
      );
    }
  }, [cafaAuthError]);

  const goToSingleIntegrationForm = () => {
    navigate({
      pathname: `${pathname}/${selectedIntegrationType}`,
      state: { new: true },
    });
  };

  const handleIntegrationListClick = (integration) => {
    navigate(`${pathname}/${integration.name}`);
  };

  const selectActiveIntegration = (integration) => {
    dispatch(
      saveActiveIntegration({ value: integration.name, integrationType }),
    );
  };

  const integrationOptions = useMemo(() => {
    const activeIntegrationNames = activeIntegrations.map((ai) => ai.name);
    const result = integrationsByType(integrationType).filter(
      (integration) => !activeIntegrationNames.includes(integration.id),
    );
    return result;
  }, [activeIntegrations, integrationType]);

  const integrationsList = useMemo(() => {
    const integ = {};
    const levelsValue = {
      Company: 0,
      Warehouse: 1,
      Pos: 2,
      User: 3,
    };
    if (activeIntegrations.length) {
      activeIntegrations.forEach((item) => {
        if (!integ[item.name]) {
          integ[item.name] = item;
        } else if (
          levelsValue[integ[item.name].level] < levelsValue[item.level]
        ) {
          integ[item.name] = item;
        }
      });
    }
    return Object.values(integ);
  }, [activeIntegrations, level]);

  if (isFetching)
    return (
      <div className="d-flex flex-column flex-fill justify-content-center align-items-center">
        <Spinner animation="border" />
      </div>
    );
  return (
    <div>
      {cafaConnectionError ? (
        <Alert variant="danger">
          Configuration API is not responding. Please, try again later.
        </Alert>
      ) : (
        <>
          <div className="jumbotron">
            <h4>Add New Integration</h4>
            <Form.Group className="mr-3">
              <Form.Control
                as="select"
                value={selectedIntegrationType || ''}
                onChange={(e) => setSelectedIntegrationType(e.target.value)}
              >
                <>
                  <option value="" disabled>
                    Select integration type
                  </option>
                  {integrationOptions.map((int) => (
                    <option key={int.id} value={int.id}>
                      {int.name}
                    </option>
                  ))}
                </>
              </Form.Control>
            </Form.Group>
            <Button
              onClick={goToSingleIntegrationForm}
              disabled={!selectedIntegrationType}
            >
              Add
            </Button>
          </div>
          <Card className="d-flex flex-column flex-fill">
            <Card.Header>Available Integrations for {level} level</Card.Header>
            <Card.Body>
              <IntegrationsList
                onClick={handleIntegrationListClick}
                integrations={integrationsList}
                loading={!activeIntegrations}
                active={activePaymentIntegration}
                level={level}
                setActive={selectActiveIntegration}
              />
            </Card.Body>
          </Card>
        </>
      )}
    </div>
  );
}

SelectIntegration.propTypes = {
  integrationType: PropTypes.string.isRequired,
};

export default SelectIntegration;
